import { useMemo } from "react";
import {
  Box,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router-dom";

import { EMPLOYEE_TYPE } from "@/constants";
import {
  attendanceOffDaySx,
  isOffWeekday,
} from "@/containers/staff/staff-attendance/attendance-cell-display";
import {
  DAY_COL_COMPACT,
  NAME_COL_WIDTH,
  SUMMARY_COL_WIDTH,
  weekdayLetterFromIndex,
  type DayMeta,
} from "@/containers/staff/staff-attendance/attendance-grid-layout";
import { staffShowPath } from "@/containers/staff/staff/staff-show-helpers";

import {
  leaveGridCellLabel,
  leaveGridCellSx,
  leaveGridCellTooltip,
  type LeaveDayDrilldown,
  type LeaveEmployeeRow,
} from "../leave-calendar-helpers";

const stickyHeadZ = 4;
const stickyBodyZ = 2;

function stickyLeftSx(left: number, zIndex: number) {
  return (theme: Theme) => ({
    position: "sticky" as const,
    left,
    zIndex,
    bgcolor: "background.paper",
    boxShadow: `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  });
}

function stickyRightSx(right: number, zIndex: number, opts?: { showLeftEdge?: boolean }) {
  return (theme: Theme) => ({
    position: "sticky" as const,
    right,
    zIndex,
    bgcolor: "background.paper",
    boxShadow: opts?.showLeftEdge
      ? `inset 1px 0 0 ${theme.palette.divider}, inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`
      : `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  });
}

function cellGridSx(theme: Theme) {
  return {
    border: "none",
    boxShadow: `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  };
}

type Props = {
  month: string;
  rows: LeaveEmployeeRow[];
  loading: boolean;
  canOfficeView: boolean;
  /** Optional off weekdays (Sun=0); defaults to Sunday only for column shading. */
  offWeekdays?: number[];
  onDayClick: (drilldown: LeaveDayDrilldown) => void;
};

/**
 * Staff × day-of-month leave grid (same table UX as department attendance).
 * Rows are active staff in the selected department (including zero leave).
 */
export function OfficeLeaveMonthGrid({
  month,
  rows,
  loading,
  canOfficeView,
  offWeekdays = [0],
  onDayClick,
}: Props) {
  const dayMeta: DayMeta[] = useMemo(() => {
    const end = dayjs(`${month}-01`).daysInMonth();
    const meta: DayMeta[] = [];
    for (let day = 1; day <= end; day += 1) {
      const weekday = dayjs(`${month}-${String(day).padStart(2, "0")}`).day();
      meta.push({
        day,
        weekday,
        weekdayLetter: weekdayLetterFromIndex(weekday),
        isOff: isOffWeekday(weekday, offWeekdays),
      });
    }
    return meta;
  }, [month, offWeekdays]);

  const gridMinWidth = NAME_COL_WIDTH + dayMeta.length * DAY_COL_COMPACT + SUMMARY_COL_WIDTH * 3;

  const summaryRight = {
    leave: SUMMARY_COL_WIDTH * 2,
    approved: SUMMARY_COL_WIDTH,
    pending: 0,
  };

  return (
    <TableContainer
      sx={{
        maxHeight: "calc(100vh - 240px)",
        maxWidth: "100%",
        width: "100%",
        minWidth: 0,
        overflowX: "auto",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        position: "relative",
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={(theme) => ({
          borderCollapse: "separate",
          borderSpacing: 0,
          minWidth: gridMinWidth,
          tableLayout: "fixed",
          marginBottom: "1px",
          opacity: loading ? 0.55 : 1,
          "& th, & td": {
            ...cellGridSx(theme),
            whiteSpace: "nowrap",
          },
          "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            ...cellGridSx(theme),
          },
        })}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={[
                stickyLeftSx(0, stickyHeadZ),
                {
                  minWidth: NAME_COL_WIDTH,
                  width: NAME_COL_WIDTH,
                  top: 0,
                  px: 1,
                },
              ]}
            >
              Staff
            </TableCell>
            {dayMeta.map(({ day, weekdayLetter, isOff }) => (
              <TableCell
                key={day}
                align="center"
                sx={(theme) => ({
                  minWidth: DAY_COL_COMPACT,
                  width: DAY_COL_COMPACT,
                  top: 0,
                  zIndex: 3,
                  px: 0,
                  py: 0.5,
                  typography: "caption",
                  lineHeight: 1.15,
                  ...(isOff ? attendanceOffDaySx(theme) : {}),
                })}
              >
                <Tooltip title={`${weekdayLetter} ${day}`}>
                  <Box component="span">
                    {weekdayLetter}
                    <br />
                    {day}
                  </Box>
                </Tooltip>
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={[
                stickyRightSx(summaryRight.leave, stickyHeadZ, { showLeftEdge: true }),
                {
                  minWidth: SUMMARY_COL_WIDTH,
                  width: SUMMARY_COL_WIDTH,
                  top: 0,
                  px: 0.25,
                },
              ]}
            >
              <Tooltip title="Leave days">
                <Box component="span" sx={{ fontWeight: 700, cursor: "help" }}>
                  L
                </Box>
              </Tooltip>
            </TableCell>
            <TableCell
              align="center"
              sx={[
                stickyRightSx(summaryRight.approved, stickyHeadZ),
                {
                  minWidth: SUMMARY_COL_WIDTH,
                  width: SUMMARY_COL_WIDTH,
                  top: 0,
                  px: 0.25,
                },
              ]}
            >
              <Tooltip title="Approved leave days">
                <Box component="span" sx={{ fontWeight: 700, cursor: "help" }}>
                  A
                </Box>
              </Tooltip>
            </TableCell>
            <TableCell
              align="center"
              sx={[
                stickyRightSx(summaryRight.pending, stickyHeadZ),
                {
                  minWidth: SUMMARY_COL_WIDTH,
                  width: SUMMARY_COL_WIDTH,
                  top: 0,
                  px: 0.25,
                },
              ]}
            >
              <Tooltip title="Pending leave days">
                <Box component="span" sx={{ fontWeight: 700, cursor: "help" }}>
                  P
                </Box>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((emp) => (
              <TableRow key={emp.employeeId} hover>
                <TableCell
                  sx={[
                    stickyLeftSx(0, stickyBodyZ),
                    {
                      minWidth: NAME_COL_WIDTH,
                      width: NAME_COL_WIDTH,
                      px: 1,
                      py: 0.5,
                    },
                  ]}
                >
                  {canOfficeView ? (
                    <MuiLink
                      component={RouterLink}
                      to={staffShowPath(emp.employeeId, month)}
                      underline="hover"
                      color="inherit"
                      sx={{
                        display: "block",
                        fontWeight: 500,
                        typography: "body2",
                        lineHeight: 1.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={emp.name}
                    >
                      {emp.name}
                    </MuiLink>
                  ) : (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        lineHeight: 1.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={emp.name}
                    >
                      {emp.name}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" component="div">
                    #{emp.employeeId}
                    {emp.department ? ` · ${EMPLOYEE_TYPE[emp.department] ?? emp.department}` : ""}
                  </Typography>
                </TableCell>
                {dayMeta.map(({ day, isOff }) => {
                  const entries = emp.days[day] ?? [];
                  const label = leaveGridCellLabel(entries);
                  const tip = leaveGridCellTooltip(entries);
                  const clickable = entries.length > 0;
                  return (
                    <TableCell
                      key={day}
                      align="center"
                      onClick={
                        clickable ? () => onDayClick({ employee: emp, day, entries }) : undefined
                      }
                      sx={(theme) => ({
                        cursor: clickable ? "pointer" : "default",
                        minWidth: DAY_COL_COMPACT,
                        width: DAY_COL_COMPACT,
                        px: 0,
                        typography: "caption",
                        ...leaveGridCellSx(theme, entries, { isOffColumn: isOff }),
                      })}
                    >
                      {label ? (
                        <Tooltip title={tip}>
                          <Box component="span">{label}</Box>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  );
                })}
                <TableCell
                  align="center"
                  sx={[
                    stickyRightSx(summaryRight.leave, stickyBodyZ, { showLeftEdge: true }),
                    {
                      minWidth: SUMMARY_COL_WIDTH,
                      width: SUMMARY_COL_WIDTH,
                      px: 0.25,
                      fontWeight: 600,
                    },
                  ]}
                >
                  {emp.summary.leaveDays}
                </TableCell>
                <TableCell
                  align="center"
                  sx={[
                    stickyRightSx(summaryRight.approved, stickyBodyZ),
                    {
                      minWidth: SUMMARY_COL_WIDTH,
                      width: SUMMARY_COL_WIDTH,
                      px: 0.25,
                    },
                  ]}
                >
                  {emp.summary.approvedDays}
                </TableCell>
                <TableCell
                  align="center"
                  sx={[
                    stickyRightSx(summaryRight.pending, stickyBodyZ),
                    {
                      minWidth: SUMMARY_COL_WIDTH,
                      width: SUMMARY_COL_WIDTH,
                      px: 0.25,
                    },
                  ]}
                >
                  {emp.summary.pendingDays}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={dayMeta.length + 4} sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {loading ? "Loading…" : "No staff in this department."}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
